import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Ban,
  CircleDollarSign,
  FileText,
  HandCoins,
  Landmark,
  Phone,
  Quote,
  Send,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { formatNaira } from '@unifemga/shared';
import { api } from '@/lib/api';
import { cn, money } from '@/lib/utils';
import { GeometricPattern } from '@/layouts/PublicShell';

/** Whole naira, no kobo. Marketing figures, not ledger figures. */
const naira = (amount: number) => formatNaira(BigInt(amount) * 100n, { showDecimals: false });

interface PublicProduct {
  id: string;
  name: string;
  description: string | null;
  minimumAmount: string;
  maximumAmount: string;
  minimumInstallments: number;
  maximumInstallments: number;
  repaymentFrequency: string;
  requiredGuarantors: number;
}

/**
 * Public landing page.
 *
 * The whole argument is one sentence — you repay exactly what you borrow — and
 * the page is built to prove it rather than assert it: a calculator the visitor
 * drives, a side-by-side against a conventional lender, and the steps spelled
 * out. Nothing here overstates what UNIFEMGA does; the figures in the
 * comparison are arithmetic on a rate the visitor sets, clearly labelled as
 * illustrative.
 */
export default function Home() {
  const { data: products = [] } = useQuery({
    queryKey: ['public-products'],
    queryFn: () => api.get<PublicProduct[]>('/loan-products/public'),
  });

  return (
    <>
      <Hero />
      <TrustBand />
      <Calculator />
      <Principles />
      <HowItWorks />
      <Products products={products} />
      <Comparison />
      <Eligibility />
      <CallToAction />
    </>
  );
}

/* -------------------------------------------------------------------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden py-10 sm:py-16 md:py-24 lg:py-28">
      {/* Layered background: grid, rosette, and a warm bloom behind the copy. */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080801a_1px,transparent_1px),linear-gradient(to_bottom,#8080801a_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_40%,#000_60%,transparent_100%)]" />
      <GeometricPattern className="pointer-events-none absolute -left-20 top-10 hidden h-[420px] w-[420px] text-primary/[0.06] lg:block" />
      <GeometricPattern className="pointer-events-none absolute -right-24 bottom-0 hidden h-[380px] w-[380px] text-primary/[0.05] lg:block" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-primary/10 blur-[100px]" />

      <div className="container relative z-10">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider shadow-sm sm:px-3.5 sm:py-2 sm:text-xs">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
                <HandCoins size={12} />
              </span>
              Qard&nbsp;Hasan — benevolent lending
            </span>

            <h1 className="mb-5 font-heading text-[2.25rem] font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Interest-free loans for
              <span className="block text-primary">UNIFEMGA members.</span>
            </h1>

            <p className="mb-8 max-w-xl text-base leading-relaxed text-foreground-secondary sm:text-lg">
              Borrow what you need and repay exactly that. No interest, no processing fee, no late
              charge — not as a promotion, but because riba has no place in how the Aliu Abdulkabir Foundation
              lends.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl bg-primary px-7 py-4 font-bold text-primary-foreground shadow-brand transition-all hover:-translate-y-0.5"
              >
                Apply for a loan <ArrowRight size={18} />
              </Link>
              <Link
                to="/loans"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl border-2 border-border px-7 py-4 font-bold text-foreground transition-colors hover:border-primary hover:text-primary"
              >
                See loan products
              </Link>
            </div>

            <p className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              {['No interest', 'No fees', 'No penalties'].map((item) => (
                <span key={item} className="inline-flex items-center gap-1.5">
                  <BadgeCheck size={15} className="text-success" /> {item}
                </span>
              ))}
            </p>
          </div>

          {/* The proposition as an object, not a paragraph. */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-3 rounded-[1.75rem] bg-gradient-to-br from-primary/15 via-transparent to-success/15 blur-xl" />
              <div className="relative rounded-3xl border border-border bg-card p-6 shadow-floating sm:p-8">
                <p className="eyebrow mb-6">A ₦500,000 loan</p>

                <div className="space-y-4">
                  <LedgerRow label="You borrow" value={naira(500_000)} />
                  <LedgerRow label="Interest charged" value="None" muted />
                  <LedgerRow label="Processing fee" value="None" muted />
                  <LedgerRow label="Late penalty" value="None" muted />
                </div>

                <div className="mt-6 flex items-end justify-between border-t-2 border-dashed border-border pt-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-foreground-secondary">
                      Total repayable
                    </p>
                    <p className="mt-0.5 text-xs text-foreground-secondary">
                      over 10 monthly repayments
                    </p>
                  </div>
                  <p className="numeric font-heading text-3xl font-black text-success">{naira(500_000)}</p>
                </div>

                <div className="mt-6 flex items-start gap-2.5 rounded-xl bg-success-soft p-3.5">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-success" />
                  <p className="text-xs leading-relaxed text-success-soft-foreground">
                    There is no field anywhere in this system that can add a charge to your balance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LedgerRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn('text-sm', muted ? 'text-foreground-secondary' : 'text-foreground')}>
        {label}
      </span>
      <span
        className={cn(
          'numeric font-semibold',
          muted ? 'text-foreground-secondary' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function TrustBand() {
  const items = [
    { icon: Ban, figure: '0%', label: 'Interest, always' },
    { icon: Wallet, figure: '₦0', label: 'In fees and charges' },
    { icon: Users, figure: '2', label: 'Guarantors per loan' },
    { icon: Landmark, figure: '1962', label: 'OAU Muslim community' },
  ];

  return (
    <section className="border-y border-border bg-background-alt">
      <div className="container grid grid-cols-2 gap-6 py-10 md:py-12 lg:grid-cols-4">
        {items.map(({ icon: Icon, figure, label }) => (
          <div key={label} className="flex items-center gap-3.5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon size={19} />
            </span>
            <div className="min-w-0">
              <p className="display-figure text-2xl text-foreground">{figure}</p>
              <p className="text-xs leading-snug text-foreground-secondary sm:text-sm">{label}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The comparison the visitor can drive themselves.
 *
 * The rate is theirs to set, and the label says the conventional figure is
 * arithmetic on that rate — not a quote from any named lender.
 */
function Calculator() {
  const [amount, setAmount] = useState(500_000);
  const [months, setMonths] = useState(12);
  const [rate, setRate] = useState(25);

  const monthly = Math.round(amount / months);
  // Flat-rate arithmetic, the way most Nigerian consumer lenders quote it.
  const conventionalTotal = Math.round(amount * (1 + (rate / 100) * (months / 12)));
  const saved = conventionalTotal - amount;

  return (
    <section className="py-12 sm:py-16 md:py-24">
      <div className="container">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-14">
          <p className="eyebrow mb-3">Work it out</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            What the same loan costs elsewhere.
          </h2>
          <p className="mt-3 text-foreground-secondary">
            Move the sliders. The AAF-UNIFEMGA figure never changes, because there is nothing in it that
            depends on time or rate.
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl items-start gap-6 lg:grid-cols-5">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-8 lg:col-span-2">
            <Slider
              label="Amount"
              value={naira(amount)}
              min={50_000}
              max={5_000_000}
              step={50_000}
              current={amount}
              onChange={setAmount}
            />
            <Slider
              label="Repayment period"
              value={`${months} months`}
              min={3}
              max={36}
              step={1}
              current={months}
              onChange={setMonths}
            />
            <Slider
              label="Comparison rate"
              value={`${rate}% a year`}
              min={5}
              max={60}
              step={1}
              current={rate}
              onChange={setRate}
              last
            />

            <p className="mt-7 border-t border-border pt-5 text-xs leading-relaxed text-foreground-secondary">
              The comparison rate is yours to set. Pick whatever you have actually been quoted
              elsewhere — the AAF-UNIFEMGA column will not move.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-3">
            <div className="relative overflow-hidden rounded-2xl border-2 border-success/30 bg-success-soft/50 p-6">
              <BadgeCheck className="absolute right-4 top-4 text-success/25" size={44} />
              <p className="text-xs font-bold uppercase tracking-wider text-success">AAF-UNIFEMGA</p>
              <p className="numeric mt-3 font-heading text-3xl font-black text-foreground">
                {naira(amount)}
              </p>
              <p className="mt-1 text-sm text-foreground-secondary">total repayable</p>
              <dl className="mt-5 space-y-2 border-t border-success/20 pt-4 text-sm">
                <Row term="Monthly" detail={naira(monthly)} />
                <Row term="Interest" detail="None" success />
                <Row term="Fees" detail="None" success />
              </dl>
            </div>

            <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-foreground-secondary">
                A conventional lender
              </p>
              <p className="numeric mt-3 font-heading text-3xl font-black text-foreground">
                {naira(conventionalTotal)}
              </p>
              <p className="mt-1 text-sm text-foreground-secondary">total repayable</p>
              <dl className="mt-5 space-y-2 border-t border-border pt-4 text-sm">
                <Row
                  term="Monthly"
                  detail={naira(Math.round(conventionalTotal / months))}
                />
                <Row term="Interest" detail={naira(saved)} danger />
                <Row term="Fees" detail="Usually extra" />
              </dl>
            </div>

            <div className="rounded-2xl bg-foreground p-6 text-background sm:col-span-2">
              <p className="text-sm font-medium text-background/60">
                On these numbers you keep
              </p>
              <p className="numeric mt-1 font-heading text-4xl font-black text-primary">
                {naira(saved)}
              </p>
              <p className="mt-3 text-xs leading-relaxed text-background/50">
                Illustrative only. The comparison figure is flat-rate arithmetic on the rate you set
                above and is not a quote from any named lender. The AAF-UNIFEMGA figure is exact.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  current,
  onChange,
  last,
}: {
  label: string;
  value: string;
  min: number;
  max: number;
  step: number;
  current: number;
  onChange: (value: number) => void;
  last?: boolean;
}) {
  return (
    <div className={cn(!last && 'mb-5 sm:mb-6')}>
      <div className="mb-2.5 flex items-baseline justify-between gap-3">
        <label className="text-sm font-bold text-foreground">{label}</label>
        <span className="numeric text-sm font-bold text-primary">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={current}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        className="range-slider"
      />
    </div>
  );
}

function Row({
  term,
  detail,
  success,
  danger,
}: {
  term: string;
  detail: string;
  success?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-foreground-secondary">{term}</dt>
      <dd
        className={cn(
          'numeric font-semibold',
          success ? 'text-success' : danger ? 'text-danger' : 'text-foreground',
        )}
      >
        {detail}
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Principles() {
  const principles = [
    {
      icon: HandCoins,
      title: 'No riba, ever',
      body: 'Every loan is Qard Hasan, funded by the Aliu Abdulkabir Foundation. Your total repayable is your principal — the system has no facility to charge interest at all.',
    },
    {
      icon: ShieldCheck,
      title: 'No hidden charges',
      body: 'No processing fee, no service charge, no late penalty. If you fall behind we telephone you; we do not add to what you owe.',
    },
    {
      icon: Users,
      title: 'Members supporting members',
      body: 'Loans are guaranteed by people who know you. It is the trust of the community that makes interest-free lending sustainable.',
    },
  ];

  return (
    <section className="relative overflow-hidden border-y border-border bg-background-alt py-12 sm:py-16 md:py-24">
      <GeometricPattern className="pointer-events-none absolute -right-32 -top-20 h-[500px] w-[500px] text-primary/[0.04]" />

      <div className="container relative">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-12">
          <p className="eyebrow mb-3">Our commitment</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Lending the way it should be.
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {principles.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="group rounded-2xl border border-border bg-card p-7 transition-all hover:-translate-y-1 hover:shadow-floating sm:p-8"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon size={22} />
              </div>
              <h3 className="mb-2.5 font-heading text-lg font-bold text-card-foreground">{title}</h3>
              <p className="text-sm leading-relaxed text-foreground-secondary">{body}</p>
            </div>
          ))}
        </div>

        <figure className="mx-auto mt-10 max-w-3xl rounded-2xl border border-border bg-card p-6 text-center sm:mt-14 sm:p-10">
          <Quote className="mx-auto mb-4 text-primary/30" size={30} />
          <blockquote className="font-heading text-lg leading-relaxed text-foreground sm:text-xl">
            &ldquo;Who is it that will lend to Allah a goodly loan, so He may multiply it for
            him?&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-sm font-semibold text-foreground-secondary">
            Qur&rsquo;an 57:11 — the verse from which <em>qard hasan</em> takes its name
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function HowItWorks() {
  const steps = [
    {
      icon: UserCheck,
      title: 'Register and verify',
      body: 'Create an account with your membership number, then complete your KYC. An officer reviews it.',
    },
    {
      icon: FileText,
      title: 'Apply and name guarantors',
      body: 'Choose an amount and a repayment period, and give the contact details of the members standing for you.',
    },
    {
      icon: Phone,
      title: 'We telephone each guarantor',
      body: 'An officer calls every guarantor to confirm. Speak to them first — they should expect the call.',
    },
    {
      icon: Send,
      title: 'Approved and paid out',
      body: 'The funds go to your bank account and your repayment schedule appears in your portal.',
    },
    {
      icon: CircleDollarSign,
      title: 'Repay exactly what you borrowed',
      body: 'Equal repayments, no surprises. Every naira reduces your balance because nothing is added to it.',
    },
  ];

  return (
    <section className="py-12 sm:py-16 md:py-24">
      <div className="container">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-16">
          <p className="eyebrow mb-3">The process</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            From application to payout.
          </h2>
        </div>

        <ol className="relative mx-auto max-w-3xl">
          {/* The spine, drawn behind the markers. */}
          <span
            className="absolute left-[23px] top-3 hidden h-[calc(100%-2rem)] w-px bg-gradient-to-b from-primary via-primary/40 to-transparent sm:block"
            aria-hidden="true"
          />

          {steps.map(({ icon: Icon, title, body }, index) => (
            <li key={title} className="relative flex gap-4 pb-7 last:pb-0 sm:gap-5 sm:pb-9">
              <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-border bg-card text-primary shadow-card">
                <Icon size={20} />
                <span className="numeric absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-black text-primary-foreground">
                  {index + 1}
                </span>
              </span>
              <div className="min-w-0 pt-1.5">
                <h3 className="font-heading text-base font-bold text-foreground sm:text-lg">
                  {title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-secondary">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

export function Products({ products }: { products: PublicProduct[] }) {
  return (
    <section id="loans" className="border-y border-border bg-background-alt py-12 sm:py-16 md:py-24">
      <div className="container">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-12">
          <p className="eyebrow mb-3">What we offer</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Loan products.
          </h2>
          <p className="mt-3 text-foreground-secondary">
            Every one of them interest-free. They differ only in size, term and how many guarantors
            you need.
          </p>
        </div>

        {products.length === 0 ? (
          <p className="text-center text-foreground-secondary">
            Loan products will be listed here shortly.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product) => (
              <div
                key={product.id}
                className="flex flex-col rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:shadow-floating"
              >
                <div className="mb-5 flex items-start justify-between gap-3">
                  <h3 className="font-heading text-lg font-bold text-card-foreground">
                    {product.name}
                  </h3>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-soft px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-success-soft-foreground">
                    <BadgeCheck size={11} /> 0%
                  </span>
                </div>

                <p className="mb-6 flex-1 text-sm leading-relaxed text-foreground-secondary">
                  {product.description}
                </p>

                <dl className="space-y-2.5 border-t border-border pt-5 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-secondary">Up to</dt>
                    <dd className="numeric font-bold text-foreground">
                      {money(product.maximumAmount, true)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-secondary">Repayments</dt>
                    <dd className="numeric font-semibold text-foreground">
                      {product.minimumInstallments}–{product.maximumInstallments}{' '}
                      {product.repaymentFrequency.toLowerCase()}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-secondary">Guarantors</dt>
                    <dd className="numeric font-semibold text-foreground">
                      {product.requiredGuarantors}
                    </dd>
                  </div>
                </dl>

                <Link
                  to="/register"
                  className="mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm font-bold text-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  Apply <ArrowRight size={15} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Comparison() {
  const rows = [
    { label: 'Interest on what you borrow', us: false, them: true },
    { label: 'Processing or admin fee', us: false, them: true },
    { label: 'Penalty when you are late', us: false, them: true },
    { label: 'A person telephones you if you fall behind', us: true, them: false },
    { label: 'Guaranteed by people who know you', us: true, them: false },
    { label: 'Every naira collected reduces your balance', us: true, them: false },
  ];

  return (
    <section className="py-12 sm:py-16 md:py-24">
      <div className="container">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-12">
          <p className="eyebrow mb-3">The difference</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Not a cheaper loan. A different kind.
          </h2>
        </div>

        <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-border bg-card">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border bg-muted/50 px-5 py-3.5 sm:px-7">
            <span className="text-xs font-black uppercase tracking-wider text-foreground-secondary">
              &nbsp;
            </span>
            <span className="w-16 text-center text-xs font-black uppercase tracking-wider text-primary sm:w-24">
              UNIFEMGA
            </span>
            <span className="w-16 text-center text-xs font-black uppercase tracking-wider text-foreground-secondary sm:w-24">
              Others
            </span>
          </div>

          {rows.map(({ label, us, them }) => (
            <div
              key={label}
              className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border px-5 py-4 last:border-b-0 sm:px-7"
            >
              <span className="pr-2 text-sm text-foreground">{label}</span>
              <span className="flex w-16 justify-center sm:w-24">
                <Mark on={us} />
              </span>
              <span className="flex w-16 justify-center sm:w-24">
                <Mark on={them} muted />
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Mark({ on, muted }: { on: boolean; muted?: boolean }) {
  return (
    <span
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-full text-sm font-black',
        on
          ? muted
            ? 'bg-muted text-foreground-secondary'
            : 'bg-success-soft text-success'
          : 'bg-muted text-foreground-secondary/50',
      )}
      aria-label={on ? 'yes' : 'no'}
    >
      {on ? '✓' : '—'}
    </span>
  );
}

/* -------------------------------------------------------------------------- */

function Eligibility() {
  const requirements = [
    'A financial member of UNIFEMGA in good standing',
    'Your KYC verified — identification, address and next of kin',
    'A Nigerian bank account in your own name',
    'Guarantors who are themselves members, whom you have spoken to',
    'No loan currently in arrears',
  ];

  return (
    <section
      id="eligibility"
      className="border-y border-border bg-background-alt py-12 sm:py-16 md:py-24"
    >
      <div className="container">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow mb-3">Who can borrow</p>
            <h2 className="mb-4 font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              Eligibility.
            </h2>
            <p className="mb-8 leading-relaxed text-foreground-secondary">
              Because there is no interest to price risk into, membership and guarantors are the
              whole of the credit assessment. That is why they are taken seriously.
            </p>
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 font-bold text-primary hover:underline"
            >
              Read the frequently asked questions <ArrowRight size={16} />
            </Link>
          </div>

          <ul className="space-y-3">
            {requirements.map((requirement) => (
              <li
                key={requirement}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-4"
              >
                <BadgeCheck size={18} className="mt-0.5 shrink-0 text-success" />
                <span className="text-sm leading-relaxed text-foreground">{requirement}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function CallToAction() {
  return (
    <section className="relative overflow-hidden py-12 sm:py-16 md:py-24">
      <GeometricPattern className="pointer-events-none absolute -left-24 -bottom-24 h-[420px] w-[420px] text-primary/[0.05]" />

      <div className="container relative">
        <div className="relative overflow-hidden rounded-3xl bg-foreground px-6 py-12 text-center sm:px-12 sm:py-14 md:py-20">
          <GeometricPattern className="pointer-events-none absolute -right-16 -top-16 h-80 w-80 text-background/[0.05]" />
          <div className="pointer-events-none absolute left-1/2 top-0 h-48 w-96 -translate-x-1/2 rounded-full bg-primary/25 blur-[80px]" />

          <div className="relative mx-auto max-w-2xl">
            <Sparkles className="mx-auto mb-5 text-primary" size={28} />
            <h2 className="mb-4 font-heading text-3xl font-bold leading-tight text-background md:text-4xl">
              Borrow ₦500,000. Repay ₦500,000.
            </h2>
            <p className="mb-9 text-base leading-relaxed text-background/60 md:text-lg">
              Join the members already borrowing without riba. It takes a few minutes to register.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl bg-primary px-7 py-4 font-bold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5"
              >
                Create account <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-xl border border-background/20 px-7 py-4 font-bold text-background transition-colors hover:bg-background/10"
              >
                Talk to us first
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
